/**
 * Shared type definitions for Raydn API payloads.
 *
 * These mirror the wireframe's API contract document. Keep them aligned with
 * the Python pydantic models in `agents/*.py`; any drift should be flagged in
 * the contract review step when Stage 4 wires in the real FastAPI layer.
 */

// ----------------------------------------------------------------------------
// Files and runs
// ----------------------------------------------------------------------------

/** Response from `POST /upload`. */
export interface UploadResponse {
  file_id: string;
  filename: string;
  size_bytes: number;
  sha256: string;
}

/** `POST /runs` body — a config matching the CPA schema plus the file id. */
export interface CreateRunRequest {
  file_id: string;
  cpa_config: Record<string, unknown>;
}

export interface CreateRunResponse {
  run_id: string;
  status: RunStatus;
}

export type RunStatus = 'queued' | 'running' | 'awaiting_review' | 'complete' | 'error';

export interface RunSummary {
  run_id: string;
  status: RunStatus;
  created_at: string;
  updated_at: string;
  file_id: string;
  cpa_profile?: string;
  /** Only populated when `status === "error"` — truncated traceback. */
  error_reason?: string;
  /** MOA executor status: `"ok"` for clean runs, `"partial"` when a cleanser failed. */
  run_result_status?: string;
  /** Per-cell anomaly count flagged by cleansers. */
  anomaly_count?: number;
  /** Schema drift report from `core.schema_guard`. Always `null` in v0.1 (no persistent baseline). */
  schema_drift?: SchemaDriftReport | null;
  /**
   * v0.4 Stage D — Cross-field validator output.
   *
   * `null`/`undefined` means no confirmed cross-field rules applied to this
   * run (either pre-v0.4 or no relatable column pairs detected). The
   * MetricsPanel renders the row only when this is a number — `0` is a
   * positive signal ("no violations") rather than a missing-data signal.
   */
  cross_field_flag_count?: number | null;
  /**
   * v0.5 Stage E.4 — reviewer-set per-column policy overrides currently
   * in force for this run. ``null`` / absent on runs without any
   * overrides; otherwise a map ``column_name → override_fields`` (only
   * ``invalid_threshold`` ships in v0.5; the field is extensible).
   * SchemaReview hydrates its threshold inputs from this map.
   */
  column_policy_overrides?: Record<string, ColumnPolicyOverride> | null;
  /**
   * v0.6 Stage B6 — column names matched by the post-FIA header PII
   * keyword scan. The upload preview blurs every column listed here
   * the moment the reviewer first sees the data (no plain-text PII
   * before the per-column PII HITLs are resolved). On a same-bytes
   * re-upload the prior reviewer's ``confirm_header_pii`` subset is
   * carried forward; otherwise this is the full keyword-match set.
   * Distinct from the per-column PII output strategy decisions
   * (drop/mask/clean) which are separate HITLs raised by PII discover.
   */
  header_pii_columns?: string[] | null;
}

/**
 * Output of the Recursive Schema Validator (`core.schema_guard`).
 *
 * Shape mirrors ``DriftReport.to_dict()``:
 *
 * - `drift_class`: overall classification — `"none"` = safe,
 *   `"minor"` = additive / widening change, `"major"` = breaking.
 * - `is_valid`: `true` for none / minor, `false` for major.
 * - `entries`: per-column drift observations.
 *
 * In v0.1 this is always absent / null because the backend has no
 * persistent baseline schema to compare against across runs. The
 * Schema Drift Banner on the frontend renders only when drift_class
 * is "major"; the field is defined here so v0.2 can populate it
 * without a contract change.
 */
export interface SchemaDriftReport {
  drift_class: 'none' | 'minor' | 'major';
  is_valid: boolean;
  baseline_version?: string | null;
  current_version?: string | null;
  entries: SchemaDriftEntry[];
}

export interface SchemaDriftEntry {
  column: string;
  kind:
    | 'column_added'
    | 'column_removed'
    | 'type_widened'
    | 'type_narrowed'
    | 'type_incompatible'
    | 'nullability_relaxed'
    | 'nullability_tightened'
    | 'order_changed';
  baseline?: string | null;
  current?: string | null;
  severity: 'minor' | 'major';
  note?: string | null;
}

// ----------------------------------------------------------------------------
// FIA output (Layer 1 — format identifier)
// ----------------------------------------------------------------------------

export interface FiaTable {
  table_id: string;
  source_name: string;
  csv_path: string;
  row_count: number;
  column_count: number;
  column_headers: string[];
  warnings: string[];
  /** Preview rows returned alongside the manifest preview; capped at 100. */
  preview_rows?: Array<Record<string, string | number | null>>;
}

export interface FiaArtifact {
  file_ref: string;
  format: 'csv' | 'excel' | 'pdf' | 'unsupported';
  /**
   * v0.4 Stage B — the user's original input format, independent of
   * processing status. Distinguishes a PDF-derived CSV (raw_data_format
   * === "pdf") from a native CSV upload. The frontend reads this to
   * render the format badge; the legacy `format` field carries the
   * processing-status connotation.
   *
   * Optional for backwards compat with pre-v0.4 FIA artifacts —
   * frontend falls back to `format` when this is absent.
   */
  raw_data_format?: 'csv' | 'excel' | 'pdf' | 'unknown';
  status: 'ok' | 'partial' | 'error';
  sha256: string;
  tables: FiaTable[];
  warnings: string[];
  layout_hitl_required: boolean;
  pipeline_gate: 'clear' | 'awaiting_layout_confirmation';
}

// ----------------------------------------------------------------------------
// RAS output (Layer 1 — schema)
// ----------------------------------------------------------------------------

export interface ColumnInference {
  column: string;
  inferred_type: string;
  confidence: number;
  source: string;
  nullable?: boolean | null;
  null_count?: number | null;
  sample_values?: string[] | null;
  candidate_types?: string[] | null;
  format_hint?: string | null;
  enum_values?: string[] | null;
  reviewer_overridden?: boolean | null;
  overridden_type?: string | null;
  /**
   * v0.5 Stage C.2 — populated iff ``inferred_type === "mixed"``.
   * Lists the picked-type tokens MOA's per-row classifier evaluates
   * against (e.g. ``["date", "string", "number"]``). Empty / absent
   * on single-type columns. Used by the Stage E.3 add-rule modal
   * to filter the operator dropdown to the intersection of valid
   * operators across the union.
   */
  type_union?: string[] | null;
  /**
   * v0.5 Stage C.2 — only meaningful when ``type_union`` is
   * populated. The catch-all type used by the per-row classifier
   * when a row matches none of the more-specific picked types.
   */
  primary_type?: string | null;
  /**
   * v0.5 Stage C.2 — ``"single"`` (legacy / single-type column,
   * default) or ``"row-routed"`` (mixed-type column dispatched
   * per-row). Defaults to ``"single"`` when absent.
   */
  policy?: 'single' | 'row-routed' | null;
}

export interface RasSchemaResponse {
  schema_version: string;
  json_schema: Record<string, unknown>;
  plain_summary: string;
  ambiguous_columns: string[];
  pii_columns: string[];
  column_inference: ColumnInference[];
}

// ----------------------------------------------------------------------------
// MOA manifest (Layer 2)
// ----------------------------------------------------------------------------

export interface ManifestStep {
  agent_id: string;
  target_columns: string[];
  output_column?: string;
  params?: Record<string, unknown>;
}

export interface Manifest {
  execution_sequence: ManifestStep[];
  data_chunks: Array<{ chunk_id: string; table_id: string; row_range: [number, number] }>;
  validation_rules?: unknown[];
}

// ----------------------------------------------------------------------------
// HITL items + actions
// ----------------------------------------------------------------------------

export type HitSeverity = 'low' | 'medium' | 'high';

/**
 * Halt-classification for a HITL item.
 *
 * v0.3 Stage E retired the advisory-continue pipeline branch: every
 * HITL halts until the reviewer resolves it, so only `blocking` is
 * emitted in v0.3+. The `advisory` and `informational` literals stay
 * in the enum for DTO fidelity against historical rows (PII's
 * uncovered-column advisory is still emitted as a deferred scope
 * item) — frontend rendering routes on `hitl_tier` (recommended /
 * unclear / legacy) rather than on this class.
 */
export type HitlClass = 'blocking' | 'advisory' | 'informational';

export interface HitlSampleRow {
  row_id: string | number;
  original: string | number | null;
  cleaned?: string | number | null;
}

/**
 * Structured candidate payload on a `format_decision` HITL item (v0.2.1).
 *
 * Each candidate is a FormatSpec-shaped dict the reviewer can pick from.
 * `kind` identifies which cleanser + enforcement variant applies; `spec`
 * is the per-kind payload that the chosen `choose_format_spec` action
 * writes back. `display_spec` is the human-readable rendering (e.g.
 * ``"SYS-###"``) for the UI.
 */
export interface FormatSpecCandidate {
  kind: string;
  spec: Record<string, unknown>;
  display_spec?: string | null;
  confidence?: number;
}

export interface HitlItem {
  hit_id: string;
  run_id: string;
  chunk_id?: string;
  agent_id: string;
  column: string;
  issue: string;
  severity: HitSeverity;
  options?: string[];
  sample_rows: HitlSampleRow[];
  created_at: string;
  status: 'open' | 'resolved' | 'escalated';
  hitl_class?: HitlClass | null;
  /**
   * Structured candidate list. Two shapes share this field today:
   *
   * - **v0.2.1 Stage C — format_decision** items (FormatInferrer):
   *   ``FormatSpecCandidate[]`` carrying ``kind`` + ``spec``. Frontend
   *   renders a candidate picker + free-type escape and dispatches
   *   ``choose_format_spec``.
   * - **v0.4 Stage C/D — cross_field_rule** items (CPA + validator):
   *   ``RuleCandidate[]`` carrying ``rule_hash`` + ``rule``. Frontend
   *   renders the appropriate rule card via ``RuleCard.tsx``.
   *
   * Consumers narrow by ``hit.resolution_type`` (or ``agent_id`` for
   * legacy paths). Legacy items using ``options`` (list[string]) keep
   * ``candidates`` absent.
   */
  candidates?: Array<
    | FormatSpecCandidate
    | RuleCandidate
    | JsonFlattenCandidate
    | SeriesCadenceCandidate
    | AddressFormatCandidate
    | MultiDateSplitCandidate
  > | null;
  allow_custom?: boolean | null;
  /**
   * v0.3 Stage A/E — two-tier HITL model. "recommended" = detected
   * candidate pre-selected; "unclear" = all options surfaced for the
   * reviewer to pick. Null on non-tiered / legacy items.
   */
  hitl_tier?: 'recommended' | 'unclear' | null;
  /**
   * v0.3 Stage A — recommended-tier rendering anchor. A canonical sample
   * value pre-filled next to the one-click confirm action. Null on
   * unclear-tier items (where ``sample_rows`` drives the picker) and on
   * legacy rows.
   */
  representative_example?: string | null;
  /**
   * v0.4 Stage C.4 — first-class card-routing discriminator. The
   * frontend branches on this rather than sniffing `agent_id` +
   * `candidates` shape. Values in current use:
   *   `table_boundary`     (FIA layout)
   *   `column_datatype`    (RAS, cleansers)
   *   `pii_treatment`      (PII)
   *   `format_decision`    (FormatInferrer)
   *   `date_output_format` (FormatInferrer, run-scoped)
   *   `cross_field_rule`   (CPA rule confirmation + validator threshold —
   *                         disambiguated via `agent_id`)
   *   `null` on legacy rows (pre-v0.4) → frontend falls back to
   *   v0.3-style routing.
   */
  resolution_type?: string | null;
}

export type HitlActionType =
  | 'choose_format'
  | 'choose_format_spec'
  | 'no_format_required'
  | 'accept'
  | 'escalate'
  | 'rerun_chunk'
  | 'rerun_full'
  // v0.4 Stage C — CPA rule confirmation card actions.
  | 'confirm_rule'
  | 'modify_rule'
  | 'reject_rule'
  // v0.4 Stage D.5 — validator threshold card actions. accept_violations
  // reuses the generic `accept` action (no separate enum value needed).
  | 'clear_failing_cells'
  | 'tag_for_review'
  // v0.5 Stage C.2 — reviewer-confirmed multi-type column. Payload
  // shape: {types: string[], primary: string | null}. Dispatched
  // when the reviewer picks ``mixed_types`` from the radio group
  // and confirms the MixedTypeSelector's checkbox picks.
  | 'choose_mixed_types'
  // v0.5 Stage E.1 — reviewer wants to re-open a previously
  // confirmed/rejected rule decision. Dispatched against any HITL
  // carrying a rule_hash (CPA confirmation cards, validator
  // threshold cards). Backend writes a rule-scoped "revisited"
  // audit; carry-forward queries treat the latest event by
  // timestamp so the prior confirm/reject is invalidated and the
  // pipeline re-emits a fresh CPA confirmation HITL.
  | 'revisit_rule'
  // v0.5 Stage F.4 — reviewer picks a target unit for a mixed-unit
  // measurement column. Payload shape:
  //   ``{target_unit: "kg" | "keep_as_is"}``.
  // Backend writes a column_policy audit row keyed on
  // ``(file_hash, column)``. Carry-forward suppresses the HITL on
  // subsequent runs of the same file and threads the target unit
  // through CPA → MOA → cleanser metadata.
  | 'choose_unit_target'
  // v0.5 Stage F.2 — reviewer picks how MOA's `_flatten_json_columns`
  // stage should expand a JSON column. Payload shape:
  //   ``{flatten_mode: "flatten_all" | "flatten_top" | "keep_string"}``.
  // Backend writes a column_policy audit row keyed on
  // ``(file_hash, column)``. Carry-forward suppresses the HITL on
  // subsequent runs of the same file and threads the mode through
  // CPA → MOA → flatten stage.
  | 'choose_json_flatten_mode'
  // v0.5 Stage F.3 — reviewer picks the cadence + interpolation
  // method for a uniform-cadence date column. Payload shape:
  //   ``{cadence: "daily" | "weekly" | "monthly" | "quarterly" |
  //              "yearly" | "inferred" | "no_resample",
  //      method:  "linear" | "ffill" | "bfill" | "drop"}``.
  // (v0.6 Stage H.1 added quarterly + yearly.)
  // Backend writes a column_policy audit row keyed on
  // ``(file_hash, date_column)``. Carry-forward suppresses the HITL
  // on subsequent runs of the same file and threads the spec
  // through CPA → MOA → resample stage.
  | 'choose_series_cadence'
  // v0.5 Stage F.1 — reviewer picks how MOA's `_split_address_columns`
  // stage should expand a parsed address column. Payload shape:
  //   ``{address_replace: "in_place" | "sibling" | "keep_freeform"}``.
  // Backend writes a column_policy audit row keyed on
  // ``(file_hash, column)``. Carry-forward suppresses the HITL on
  // subsequent runs of the same file and threads the mode through
  // CPA → MOA → split stage.
  | 'choose_address_format'
  // v0.6 Stage H.2 — reviewer confirms (and optionally renames) the
  // proposed split of a multi-date column into ``<col>_start`` /
  // ``<col>_end`` siblings. Payload shape: ``{}`` (confirmed —
  // use the spec defaults) OR
  //   ``{start_column_name?: string, end_column_name?: string}``
  // (confirmed with edits). Fields matching the spec default are
  // omitted so the payload represents the minimum delta; an empty
  // dict is the "no edits, use defaults" signal — distinct from
  // absent (= not yet confirmed). Backend writes a column_policy
  // audit row keyed on ``(file_hash, column)``. Carry-forward
  // suppresses the HITL on subsequent runs of the same file and
  // threads the names through CPA → MOA → ``_split_multi_date_columns``.
  | 'choose_multi_date_split'
  // v0.5 Stage G.3 — reviewer picks one or more table_index values
  // from a multi-table PDF surfaced by FIA's pdf_table_selection
  // HITL. Payload shape: ``{table_indices: number[]}``. Backend
  // writes a file-scoped audit row keyed on ``file_hash``.
  // Carry-forward suppresses the HITL on subsequent same-bytes
  // uploads; pipeline.py threads the selection into
  // ``FIAInput.pdf_table_selection`` before invoking FIA.
  | 'choose_pdf_tables'
  // v0.6 Stage G.3 — reviewer reopens a cleanser-quality decision.
  // Mirror of ``revisit_rule``: backend dispatches a sibling
  // ``revisit_cleanser_decision`` audit on the PRIOR ack'd hit so the
  // v0.6 Stage G.2 carry-forward query treats the prior accept as
  // stale (latest-event-wins). The next pipeline pass re-emits the
  // cleanser-quality HITL fresh. Dispatched with no payload; the
  // dispatcher looks up the prior hit by
  // ``(file_hash, agent_id, column, threshold_signal)``.
  | 'revisit_cleanser_decision'
  // v0.6 Stage B6 — reviewer confirms which header-matched columns
  // should remain blurred in the upload preview. Single consolidated
  // card listing every header that matched the PII keyword scan;
  // reviewer un-ticks any column they confirm is not sensitive.
  // Payload: ``{columns: string[]}`` — the subset left ticked.
  // Distinct from the per-column PII output-strategy decisions
  // (drop/mask/clean/pass_through), which remain as separate HITLs.
  | 'confirm_header_pii'
  // v0.6 Stage B12 — QualityGateCard sub-panel resolutions for the
  // missing_handler HIGH_MISSING_THRESHOLD HITL. Reviewer picks a
  // replacement value (radio over top-N column mode_candidates, or a
  // text-input pre-filled with default_constant) plus a replace_nulls
  // checkbox controlling whether genuine nulls are also overwritten.
  // Payload shapes:
  //   switch_to_mode     → {selected_mode_value: string,
  //                          replace_nulls: boolean}
  //   switch_to_constant → {constant_value: string,
  //                          replace_nulls: boolean}
  // Backend records a hit-scoped audit + flips status to resolved;
  // when this is the last open blocker the run auto-resumes via the
  // REKICK_ACTION_TYPES allowlist (see backend/app/audit_actions.py).
  | 'switch_to_mode'
  | 'switch_to_constant';

/**
 * v0.4 Stage C — operator vocabulary used by cross-field rules.
 * Mirrors core/rule_engine.py SUPPORTED_OPS. The frontend renders rule
 * cards by passing the rule shape through `services/ruleText.ts` for
 * plain-English copy.
 */
export type RuleOperator =
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'ne'
  | 'not_null_when'
  // v0.5 Stage E.2 — summation operators. Compare ``col_a`` against
  // ``sum(sum_columns)`` with a mandatory tolerance (default 0.01).
  // Tolerance is applied as a noise floor on every operator —
  // values within ±tolerance of the threshold count as equal.
  | 'sum_eq'
  | 'sum_gt'
  | 'sum_gte'
  | 'sum_lt'
  | 'sum_lte';

/** v0.5 Stage E.2 — true iff ``op`` is a summation operator. */
export const SUM_OPERATORS: ReadonlySet<RuleOperator> = new Set<RuleOperator>([
  'sum_eq',
  'sum_gt',
  'sum_gte',
  'sum_lt',
  'sum_lte',
]);

export function isSumOperator(op: RuleOperator): boolean {
  return SUM_OPERATORS.has(op);
}

export interface RuleCondition {
  col: string;
  value: string | number;
}

export interface CrossFieldRule {
  col_a: string;
  /**
   * Pairwise rules (v0.4) carry ``col_b``. Summation rules
   * (v0.5 Stage E.2) carry ``sum_columns`` instead — ``col_b`` is
   * absent on the wire and consumers must narrow on ``operator``
   * (or ``isSumOperator``) before reading it.
   */
  col_b?: string;
  operator: RuleOperator;
  condition?: RuleCondition | null;
  description?: string;
  severity?: HitSeverity;
  /**
   * v0.5 Stage E.2 — non-empty list of part-column names. Present
   * iff ``operator`` is a summation operator. Order is not
   * structurally significant (the backend's ``rule_hash`` sorts
   * canonically), but the UI preserves reviewer-entered order for
   * display.
   */
  sum_columns?: string[];
  /**
   * v0.5 Stage E.2 — comparison tolerance for summation rules.
   * Defaults to ``0.01`` (currency-style penny precision) when
   * absent. Bears no effect on rule identity — adjusting it on a
   * confirmed rule does not invalidate the carry-forward audit.
   */
  tolerance?: number;
}

/**
 * Shape carried inside `HitlItem.candidates[0]` for both rule-card
 * flavours (CPA confirmation + validator threshold). The validator HITL
 * stores the rule + rule_hash here so the frontend and the router can
 * extract them uniformly.
 */
export interface RuleCandidate {
  rule_hash: string;
  rule: CrossFieldRule;
  confidence_tier?: 'recommended' | 'unclear';
  description?: string;
  table_id?: string;
  source_heuristic?: string;
}

/**
 * v0.5 Stage F.2 Phase 2 — payload carried inside
 * ``HitlItem.candidates[0]`` for the ``json_flatten`` HITL.
 *
 * The persisted ``HitlItem`` doesn't surface ``provenance``, so the
 * JSON flatten constructor stashes its structured payload (the
 * distinct-key count + a 20-key preview) in ``candidates[0]`` —
 * mirroring the format_decision / cross_field_rule pattern. The
 * frontend ``JsonFlattenCard`` reads from here to render the count
 * line + sample-keys preview.
 */
export interface JsonFlattenCandidate {
  distinct_keys_count: number;
  distinct_keys_preview: string[];
}

/**
 * v0.5 Stage F.3 — payload carried inside ``HitlItem.candidates[0]``
 * for the ``series_cadence`` HITL.
 *
 * The persisted ``HitlItem`` doesn't surface ``provenance``, so the
 * series cadence constructor stashes its structured payload here —
 * the date column anchoring the series, the paired numeric value
 * columns the resample interpolates, the inferred cadence the
 * heuristic suggests, and the method options the dropdown surfaces.
 * The frontend ``SeriesCadenceCard`` reads from here to render the
 * radio + dropdown picker + summary line.
 */
export interface SeriesCadenceCandidate {
  date_column: string;
  value_columns: string[];
  inferred_cadence:
    | 'daily'
    | 'weekly'
    | 'monthly'
    | 'quarterly'  // v0.6 Stage H.1
    | 'yearly'  // v0.6 Stage H.1
    | string;
  median_delta_days: number;
  parsed_count: number;
  suggested_cadence: string;
  suggested_method: string;
  method_options: string[];
}

/**
 * v0.5 Stage F.3 — series resample policy carried inside the
 * ``column_policies.<col>`` blob. Reviewer-set via the
 * ``choose_series_cadence`` HITL action; absent fields fall back to
 * the resample stage's auto-default behaviour (HITL emission when
 * uniform, no-op when not).
 */
export interface SeriesResamplePolicy {
  cadence:
    | 'daily'
    | 'weekly'
    | 'monthly'
    | 'inferred'
    | 'no_resample';
  method: 'linear' | 'ffill' | 'bfill' | 'drop';
}

/**
 * v0.5 Stage E.4 / F.4 / F.2 / F.3 / F.5 — per-column policy override
 * fields surfaced on ``RunSummary.column_policy_overrides``.
 * Reviewer-set values come either from the ``PATCH /runs/:run_id/
 * column_policies`` endpoint (E.4 + F.4 + F.5) or from an HITL action
 * handler (``choose_unit_target`` / ``choose_json_flatten_mode`` /
 * ``choose_series_cadence``). ``null`` on a field clears a prior
 * override under latest-event-wins semantics on the audit log.
 */
export interface ColumnPolicyOverride {
  invalid_threshold?: number | null;
  unit_target?: string | null;
  json_flatten_mode?: 'flatten_all' | 'flatten_top' | 'keep_string' | null;
  /**
   * v0.5 Stage F.3 — series resample policy. Carried as an object
   * with ``cadence`` + ``method`` so the SchemaReview panel can
   * display both fields together.
   */
  series_resample?: SeriesResamplePolicy | null;
  /**
   * v0.5 Stage F.5 — ISO 639-1 language code (``en``, ``fr``,
   * ``es``, ``de``, …) selecting the NLTK stopwords list for the
   * text_normaliser cleanser. Cleanser falls back to ``en`` when
   * absent. Lemmatisation is English-only — non-English columns
   * receive tokenisation + stopword removal but the WordNet
   * lemmatiser is skipped.
   */
  text_language?: string | null;
  /**
   * v0.5 Stage F.5 — toggle for the text_normaliser's NLP block
   * (tokenise / stopword removal / lemmatise). Default ``true``.
   * Setting to ``false`` reverts the cleanser to the v0.4
   * deterministic-only pipeline (HTML / Markdown / punctuation /
   * lowercase) on the column.
   */
  text_nlp_enabled?: boolean | null;
  /**
   * v0.6 Stage I — reviewer-added domain stopwords. Merged with the
   * NLTK language default (selected by ``text_language``) before
   * stopword removal in the text_normaliser cleanser. Per-token
   * normalisation (strip + lowercase + dedupe) happens server-side;
   * the SchemaReview UI sends the reviewer's raw comma-separated
   * input and the PATCH endpoint canonicalises before writing the
   * audit row. Empty array or ``null`` clears the override (the
   * cleanser falls back to NLTK-only).
   */
  text_extra_stopwords?: string[] | null;
  /**
   * v0.5 Stage F.1 — address column expansion strategy. ``in_place``
   * (default) replaces the freeform column with per-component sibling
   * columns (``<col>.house_number``, ``<col>.road``, ``<col>.city``,
   * ``<col>.postcode``, ...). ``sibling`` keeps the original
   * alongside the per-component columns. ``keep_freeform`` preserves
   * the column unchanged (opt-out sentinel; suppresses
   * address_format HITL re-fire).
   */
  address_replace?: 'in_place' | 'sibling' | 'keep_freeform' | null;
}

/**
 * v0.5 Stage F.1 — payload carried inside ``HitlItem.candidates[0]``
 * for the ``address_format`` HITL.
 *
 * The persisted ``HitlItem`` doesn't surface ``provenance``, so the
 * address format constructor stashes its structured payload here —
 * the parse-success-rate breakdown, libpostal availability, sample
 * components from successful parses, and the suggested replace mode +
 * language options. The frontend ``AddressFormatCard`` reads from
 * here to render the radio + dropdown picker + summary line.
 */
export interface AddressFormatCandidate {
  non_empty: number;
  parsed_full: number;
  parsed_partial: number;
  unparseable: number;
  success_rate: number;
  libpostal_available: boolean;
  sample_components: Array<Record<string, string>>;
  suggested_replace: 'in_place' | 'sibling' | 'keep_freeform';
  language_options: string[];
}

/**
 * v0.6 Stage H.2 — payload carried inside ``HitlItem.candidates[0]``
 * for the ``multi_date_split`` HITL.
 *
 * RAS's modal-pattern finalizer stashes the proposed
 * ``MultiDateSpec`` here: the date format detected in both halves of
 * the modal cell shape, the separator pattern, the proposed sibling
 * column names (``<col>_start`` / ``<col>_end`` by default), and the
 * parsed-vs-sample counts the detector observed. The frontend
 * ``MultiDateSplitCard`` reads from here to pre-fill the rename
 * inputs and render the "<n> of <m> cells matched" stat. Source-cell
 * sample previews arrive on the top-level ``HitlItem.sample_rows``.
 */
export interface MultiDateSplitCandidate {
  column: string;
  table_id: string;
  date_format: string;
  separator_pattern: string;
  start_column_name: string;
  end_column_name: string;
  parsed_count: number;
  sample_count: number;
}

/**
 * v0.5 Stage E.4 — wire shape for ``PATCH /runs/:run_id/column_policies``.
 * ``overrides`` is a map ``column → override fields``. Columns absent
 * from the body are left untouched; their existing overrides (if
 * any) carry forward unchanged.
 */
export interface UpdateColumnPoliciesRequest {
  overrides: Record<string, ColumnPolicyOverride>;
  reviewer_id: string;
  comment?: string;
}

export interface UpdateColumnPoliciesResponse {
  run_id: string;
  status: RunStatus;
  audit_ids: string[];
}

/**
 * v0.5 Stage E.3 — wire shape for ``POST /runs/:run_id/rules`` (the
 * reviewer-defined-rule endpoint). Mirrors the backend's pydantic
 * ``_ReviewerDefinedRuleBody`` model. Pairwise rules carry ``col_b``;
 * summation rules carry ``sum_columns``. The reviewer's ``description``
 * is optional — when absent the backend persists ``null`` and the
 * frontend's ``renderRuleDescription`` synthesises one from the rule
 * shape.
 */
export interface ReviewerDefinedRuleRequest {
  col_a: string;
  col_b?: string;
  operator: RuleOperator;
  sum_columns?: string[];
  tolerance?: number;
  condition?: RuleCondition | null;
  description?: string;
  severity?: HitSeverity;
  table_id?: string;
  reviewer_id: string;
  comment?: string;
}

export interface ReviewerDefinedRuleResponse {
  run_id: string;
  rule_hash: string;
  audit_id: string;
  status: RunStatus;
}

/**
 * One row of `cross_field_flags` from the validator. Fetched via
 * `GET /runs/:run_id/cross_field_flags?limit=&offset=`. `row_id` of
 * `-1` is reserved for per-rule truncation summaries — frontend
 * filters those out for display.
 */
export interface CrossFieldFlag {
  row_id: number;
  rule_id: string;
  col_a: string;
  col_b: string | null;
  col_a_value: string | null;
  col_b_value: string | null;
  issue: string;
  severity: HitSeverity;
}

export interface CrossFieldFlagsResponse {
  run_id: string;
  row_flags: CrossFieldFlag[];
  total: number;
  limit: number;
  offset: number;
}

export interface HitlActionRequest {
  type: HitlActionType;
  payload?: Record<string, unknown>;
  reviewer_id: string;
  comment?: string;
}

export interface HitlActionResponse {
  status: 'accepted' | 'rejected';
  audit_id: string;
}

/**
 * v0.5 Stage D — bulk-confirm request shape. Lists hit_ids the
 * reviewer wants to resolve in one round-trip via the
 * recommended-tier sweeper.
 */
export interface HitlBatchConfirmRequest {
  hit_ids: string[];
  reviewer_id: string;
  comment?: string;
}

/**
 * v0.5 Stage D — bulk-confirm response. ``confirmed`` lists hit_ids
 * whose audit events were written + status flipped to resolved.
 * ``skipped`` lists hit_ids that were already resolved (idempotent
 * re-fire is benign). ``audit_id`` is the run-scoped batch summary
 * event's id.
 */
export interface HitlBatchConfirmResponse {
  status: 'accepted' | 'rejected';
  audit_id: string;
  confirmed: string[];
  skipped: string[];
}

/**
 * One row in a hit's audit trail. Recorded server-side on every reviewer
 * action so the dashboard's timeline can reconstruct who did what and when.
 */
export interface AuditEntry {
  audit_id: string;
  hit_id: string;
  reviewer_id: string;
  action_type: HitlActionType;
  payload?: Record<string, unknown>;
  comment?: string;
  timestamp: string;
}

/** Optional query filters for `GET /hits` when used cross-run. */
export interface HitlListQuery {
  run_id?: string;
  severity?: HitSeverity;
  agent_id?: string;
  column?: string;
  status?: HitlItem['status'];
  /** v0.3 Stage E — narrow queue by tier band. */
  hitl_tier?: 'recommended' | 'unclear';
}

// ----------------------------------------------------------------------------
// Re-run controls
// ----------------------------------------------------------------------------

export interface RerunRequest {
  chunk_id?: string;
  full_run?: boolean;
  reviewer_id: string;
}

export interface RerunResponse {
  run_id: string;
  status: RunStatus;
}

// ----------------------------------------------------------------------------
// WebSocket events
// ----------------------------------------------------------------------------

export type RunEvent =
  | { type: 'progress'; chunk_id: string; percent: number; timestamp: string }
  | {
      type: 'log';
      agent_id: string;
      status: 'ok' | 'retry' | 'error';
      message: string;
      timestamp: string;
    }
  | { type: 'hitl_created'; hit_id: string; severity: HitSeverity; timestamp: string }
  | { type: 'run_status'; status: RunStatus; timestamp: string };

// ----------------------------------------------------------------------------
// Stage 2 — Output surface
// ----------------------------------------------------------------------------

export interface CleanedPreviewResponse {
  table_id: string;
  rows: Array<Record<string, string | number | null>>;
  total_rows: number;
  offset: number;
  limit: number;
  pii_columns: string[];
}

export interface DiffRow {
  row_id: number;
  column: string;
  original?: string | null;
  cleaned?: string | null;
  change_type: 'value_changed' | 'value_nulled' | 'value_filled' | string;
}

export interface DiffResponse {
  table_id: string;
  diffs: DiffRow[];
  total_diffs: number;
  offset: number;
  limit: number;
}

export interface ProvenanceColumnEntry {
  change_type: string;
  count: number;
  narrative: string;
  agents: string[];
}

export interface ProvenanceResponse {
  table_id: string;
  by_column: Record<string, ProvenanceColumnEntry[]>;
  total_changes: number;
}

export interface AnomalyFlagItem {
  anomaly_id: string;
  table_id: string;
  row_id: number;
  column: string;
  original_value: string;
  issue: string;
  severity?: string;
  attempted_transformation?: string | null;
  responsible_agent?: string | null;
  final_output?: string | null;
  acknowledged: boolean;
  /**
   * Free-text context for column-level summary entries (row_id === -1).
   * v0.6 Stage B4: missing_handler emits a single null_values_present
   * summary per column whose ``detail`` carries the null count, e.g.
   * "3 null value(s) found in column 'price'".
   */
  detail?: string | null;
}

export interface AnomaliesResponse {
  run_id: string;
  anomalies: AnomalyFlagItem[];
  total: number;
}

export interface ApproveResponse {
  run_id: string;
  approved: boolean;
  pii_columns: string[];
  audit_id: string;
}

export interface DownloadUrlResponse {
  run_id: string;
  token: string;
  expires_in_seconds: number;
}

export interface RunAuditEvent {
  audit_id: string;
  run_id: string;
  event_type: string;
  reviewer_id: string;
  payload?: Record<string, unknown>;
  comment?: string;
  timestamp: string;
}

export interface AnomalyAckResponse {
  anomaly_id: string;
  audit_id: string;
}

/** v0.6 Stage A.3 — surface label for the PII reveal audit row. The
 * Output-tab cleansed-row preview emits ``"preview"``; the Schema-tab
 * FIA sample-row preview emits ``"fia"``. Distinguishes the two surfaces
 * when reconstructing a reviewer's reveal timeline. */
export type PiiRevealSurface = 'preview' | 'fia';

export interface PiiRevealRequest {
  surface: PiiRevealSurface;
  pii_columns: string[];
}

export interface PiiRevealResponse {
  audit_id: string;
}

// ----------------------------------------------------------------------------
// Error envelope
// ----------------------------------------------------------------------------

/** Thrown by `api.ts` for any non-2xx response. */
export class ApiError extends Error {
  public readonly status: number;
  public readonly body: unknown;

  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}
